import React, { useState } from "react";
import BottomSheet from "./BottomSheet";
import { PROFILE_ICONS } from "./profileMenuIcons";
import { formatAmountForCopy } from "../utils/currency";
import "./FeeTierSheet.css";
import { FeeTierOfferBody, launchOfferEnds } from "./FeeTierOffer";
import type { LaunchOffer, ReferralBoost } from "../types/orders";
import { t as tr } from "../i18n";
import { T } from "../i18n/T";

// Tier Level sheet (founder-approved V2 "medal + the road"; row +
// sheet renamed from "Platform Fee" — founder, more fun language) —
// the loyalty ladder, Bronze → Legend. Renders whatever GET /orders/fee-tier
// returned; the row that opens this sheet only exists when that payload said
// enabled, so this component never has to defend against the feature being
// dark. Hero is flipped to what the talent KEEPS ("91% — of every request is
// yours"), per founder.

export type FeeTierStatus = {
  enabled: boolean;
  currency: string;
  lifetimeEarned: number;
  tier: { key: string; name: string; feePct: number; keepPct: number };
  next: { key: string; name: string; keepPct: number; threshold: number; remaining: number } | null;
  progress: number;
  ladder: { key: string; name: string; keepPct: number; threshold: number }[];
  effectiveKeepPct: number;
  overrideActive: boolean;
  /** 1.5.1 — absent on an older server. */
  launchOffer?: LaunchOffer | null;
  referralBoost?: ReferralBoost | null;
  /** true while Tier Level is paused on the server (with enabled: false). */
  paused?: boolean;
};

// One source of truth for the metal palette — the Profile row's pill dot
// imports from here so the two can never drift.
export const TIER_COLORS: Record<string, string> = {
  bronze: "#CD8A4B",
  silver: "#C9CDD4",
  gold: "#F5C542",
  platinum: "#E8EDF2",
  diamond: "#7FDBFF",
  legend: "#24C961",
};

// The V2 medal: ribbon + disc + star, tinted per tier. Stroke-free shapes so
// it scales crisply; the star picks a darker shade of the same metal.
const STAR_TINT: Record<string, string> = {
  bronze: "#8a5a2e",
  silver: "#8b93a3",
  gold: "#a8842a",
  platinum: "#9aa7b3",
  diamond: "#3f96b8",
  legend: "#127a3c",
};

const Medal: React.FC<{ tierKey: string }> = ({ tierKey }) => {
  const fill = TIER_COLORS[tierKey] || TIER_COLORS.bronze;
  const star = STAR_TINT[tierKey] || STAR_TINT.bronze;
  return (
    <svg className="ft-medal" width="54" height="60" viewBox="0 0 54 60" aria-hidden="true">
      <path d="M17 2h20l-6 16H23z" fill="#3a4150" />
      <path d="M13 2h10l-5 14z" fill="#4a5262" />
      <path d="M41 2H31l5 14z" fill="#2e3542" />
      <circle cx="27" cy="36" r="19" fill={fill} />
      <circle cx="27" cy="36" r="19" fill="url(#ft-sheen)" />
      <circle cx="27" cy="36" r="13.5" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
      <path
        d="M27 27l2.6 5.4 6 .8-4.4 4.1 1.1 5.9-5.3-2.9-5.3 2.9 1.1-5.9-4.4-4.1 6-.8z"
        fill={star}
      />
      <defs>
        <linearGradient id="ft-sheen" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" stopOpacity="0.55" />
          <stop offset="0.5" stopColor="#fff" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.18" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const FeeTierSheet: React.FC<{
  open: boolean;
  onClose: () => void;
  status: FeeTierStatus;
}> = ({ open, onClose, status }) => {
  // Tapping a road dot peeks that tier's name + unlock amount in place of the
  // Legend dangle line; tapping the same dot (or closing) clears it.
  const [peekKey, setPeekKey] = useState<string | null>(null);
  // The (i) explainer next to the hero (founder decision): WHY a fee exists +
  // that it only goes down. Toggles on tap; closing the sheet resets it.
  const [infoOpen, setInfoOpen] = useState(false);

  const { tier, next, ladder, currency, progress, overrideActive, effectiveKeepPct } = status;
  const money = (n: number) => formatAmountForCopy(n, currency);
  const effectiveFeePct = overrideActive ? 100 - effectiveKeepPct : tier.feePct;

  const close = () => {
    setPeekKey(null);
    setInfoOpen(false);
    onClose();
  };

  const peeked = peekKey ? ladder.find((t) => t.key === peekKey) : null;
  const startedEarning = status.lifetimeEarned > 0;

  // The 0% launch offer leads the sheet while it runs (founder 1.5.1 #12).
  const offerEnds = launchOfferEnds(status);
  if (offerEnds) {
    return (
      <BottomSheet open={open} title={tr("feeTierSheet.tierLevel")} icon={PROFILE_ICONS.feeTier} onClose={close}>
        <FeeTierOfferBody status={status} ends={offerEnds} />
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} title={tr("feeTierSheet.tierLevel")} icon={PROFILE_ICONS.feeTier} onClose={close}>
      <div className="ft-sheet">
        <div className="ft-center">
          <Medal tierKey={tier.key} />
          <div className="ft-tier-name" style={{ color: TIER_COLORS[tier.key] }}>
            {tier.name.toUpperCase()}
          </div>
          {/* Referral tier jump (founder 1.5.1 #11): why this rung is one above their earnings. */}
          {status.referralBoost?.active && <div className="ft-boost">{tr("feeTierSheet.plusOneTierFromReferrals")}</div>}
        </div>
        <div className="ft-hero">{overrideActive ? effectiveKeepPct : tier.keepPct}%</div>
        <div className="ft-hero-sub">
          <T k="feeTierSheet.ofEveryRequestIs" tags={{ 0: (c) => <button
            type="button"
            className="ft-info-btn"
            aria-label={tr("feeTierSheet.aboutYourFee")}
            aria-expanded={infoOpen}
            onClick={() => setInfoOpen((v) => !v)}
          >{c}</button> }} />
        </div>
        {/* Why tiers exist, in one green line (founder, 1.5.1 #3). A custom
            rate sits outside the ladder, so it gets no motto. No currency in
            it on purpose: it reads right for talent paid in € or £. */}
        {!overrideActive && <div className="ft-motto">{tr("feeTierSheet.earnMoreKeepMore")}</div>}
        {infoOpen && (
          <div className="ft-info-pop" role="status">
            {tr("feeTierSheet.weTakeOfEach", { effectiveFeePct })}
          </div>
        )}

        {overrideActive ? (
          // Rare: an admin-set custom rate beats the ladder. No rung to chase —
          // they're already ahead of it.
          <div className="ft-next">{tr("feeTierSheet.youReOnA")}</div>
        ) : (
          <>
            <div className="ft-bar" aria-hidden="true">
              <i style={{ width: `${Math.round(progress * 100)}%` }} />
            </div>

            {next ? (
              <div className="ft-next">
                {startedEarning ? (
                  <>
                    <T k="feeTierSheet.toGo" vars={{ amount: money(next.remaining), name: next.name, keepPct: next.keepPct }} tags={{ 0: (c) => <b>{c}</b> }} />
                  </>
                ) : (
                  <>
                    <T k="feeTierSheet.earn" vars={{ amount: money(next.threshold), name: next.name, keepPct: next.keepPct }} tags={{ 0: (c) => <b>{c}</b> }} />
                  </>
                )}
              </div>
            ) : (
              <div className="ft-next">
                <T k="feeTierSheet.lockedInForever" tags={{ 0: (c) => <b>{c}</b> }} />
              </div>
            )}

            <div className="ft-road" role="list">
              {ladder.map((t) => {
                const reached =
                  ladder.findIndex((x) => x.key === tier.key) >=
                  ladder.findIndex((x) => x.key === t.key);
                const current = t.key === tier.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="listitem"
                    className={
                      "ft-dot" + (current ? " ft-dot--cur" : reached ? "" : " ft-dot--todo")
                    }
                    style={{ background: TIER_COLORS[t.key] }}
                    aria-label={`${t.name} — keep ${t.keepPct}%${
                      t.threshold > 0 ? ` at ${money(t.threshold)}` : ""
                    }`}
                    onClick={() => setPeekKey(peekKey === t.key ? null : t.key)}
                  />
                );
              })}
            </div>
            <div className="ft-floor">
              {peeked
                ? peeked.threshold > 0
                  ? tr("feeTierSheet.keep", { name: peeked.name, keepPct: peeked.keepPct, amount: money(peeked.threshold) })
                  : tr("feeTierSheet.keepDayOne", { name: peeked.name, keepPct: peeked.keepPct })
                : tr("feeTierSheet.legendKeepsForever")}
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default FeeTierSheet;
